import { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  Form,
  AddressInput,
  Typography,
  PLATFORM_THEME_COLORS,
  ICONS,
  Button,
  Icon,
  SelectInput,
  DeleteModal,
} from '@thinknet-gmbh/platform-common-ui';

import { useGetHolidayLocationsByCompany } from 'src/hooks/holiday-manager/get-holiday-locations-by-company.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useDeleteCompanyBusinessAddressById } from 'src/hooks/companies/delete-business-address-by-id.hook';

import { DepartmentsFormList } from './components/departments-form-list';
import {
  DeleteContainer,
  AddressWrapper,
  MainContainer,
  HolidayWrapper,
} from './business-address-list-form.styles';
import { useState } from 'react';
import { displayAddress } from 'src/common/helpers/common.helpers';

const BusinessAddressList = ({
  parentField,
  form,
}: {
  parentField: number;
  form: FormInstance<any>;
}) => {
  const { t } = useTranslation(['common', 'settings']);
  const [companyBusinessAddressToDelete, setCompanyBusinessAddressToDelete] = useState<any>(null);

  const currentCompanyId = useGetCurrentCompanyId();
  const { holidayLocations } = useGetHolidayLocationsByCompany(currentCompanyId);

  const deleteCompanyBusinessAddress = useDeleteCompanyBusinessAddressById();

  return (
    <MainContainer>
      <Form.List name={[parentField, 'businessAddresses']}>
        {(fields, { add, remove }) => {
          const deleteBusinessAddressHandler = (fieldName: number) => {
            const pathToFormData = ['locations', parentField, 'businessAddresses', fieldName];
            const formBusinessAddress = form.getFieldValue(pathToFormData);

            if (formBusinessAddress._id) {
              setCompanyBusinessAddressToDelete({
                businessAddress: formBusinessAddress,
                fieldName,
              });
            } else {
              remove(fieldName);
            }
          };

          const onSubmitDeleteModal = () => {
            deleteCompanyBusinessAddress({
              variables: { businessAddressId: companyBusinessAddressToDelete.businessAddress._id },
              onCompleted: data => {
                if (data) {
                  remove(companyBusinessAddressToDelete.fieldName);
                  setCompanyBusinessAddressToDelete(null);
                }
              },
            });
          };

          return (
            <div>
              <DeleteModal
                title={t('settings:company.deleteBusinessAddressModal.title')}
                subTitle={t('settings:company.deleteBusinessAddressModal.subtitle', {
                  address: companyBusinessAddressToDelete
                    ? displayAddress(companyBusinessAddressToDelete?.businessAddress)
                    : null,
                })}
                onSubmit={() => onSubmitDeleteModal()}
                submitBtnText={t('common:buttons.delete')}
                translate={t}
                onClose={() => setCompanyBusinessAddressToDelete(null)}
                isOpened={!!companyBusinessAddressToDelete}
              />

              {fields.map(field => {
                return (
                  <MainContainer key={field.key}>
                    <DeleteContainer>
                      <Typography
                        weight="bold"
                        fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}
                      >
                        {`${t('common:labels.businessAddress')} ${
                          fields.length > 1 ? field.name + 1 : ''
                        }`}
                      </Typography>
                      {fields.length > 1 && (
                        <Icon
                          icon={ICONS.trash}
                          color={PLATFORM_THEME_COLORS.tone.red}
                          size={18}
                          rightMargin={false}
                          onClick={() => deleteBusinessAddressHandler(field.name)}
                        />
                      )}
                    </DeleteContainer>
                    <AddressWrapper>
                      <AddressInput t={t} parentField={`${field.name}`} twoColumn />
                      <Button
                        type="text"
                        icon={ICONS.plus}
                        iconColor={PLATFORM_THEME_COLORS.primary.blue}
                        onClick={() => add({ departments: [{}] })}
                      >
                        {t('settings:company.buttons.addBusinessAddress')}
                      </Button>
                    </AddressWrapper>
                    <DepartmentsFormList
                      parentField={field.name}
                      parentLocationField={parentField}
                      form={form}
                    />
                    <HolidayWrapper>
                      <Typography
                        weight="bold"
                        fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}
                      >
                        {t('settings:holidayManager.labels.holidayManager')}
                      </Typography>

                      <Form.Item
                        name={[field.name, 'holidayLocationId']}
                        label={t('settings:holidayManager.labels.holidayLocation')}
                        rules={[
                          {
                            required: true,
                            message: t('common:validation.error.locationRequired'),
                          },
                        ]}
                      >
                        <SelectInput placeholder={t('common:placeholders.selectFromList')}>
                          {holidayLocations.map((holidayLocation: any) => (
                            <SelectInput.Option
                              key={holidayLocation._id}
                              value={holidayLocation._id}
                            >
                              {holidayLocation.name}
                            </SelectInput.Option>
                          ))}
                        </SelectInput>
                      </Form.Item>
                    </HolidayWrapper>
                  </MainContainer>
                );
              })}
            </div>
          );
        }}
      </Form.List>
    </MainContainer>
  );
};

export default BusinessAddressList;
